// !!! IMPORTANT !!!
// 95% of code in this file was copied from https://github.com/anttiviljami/openapi-client-axios/blob/master/src/client.ts
// then is was slightly rewritten to simplify usage

/* eslint-disable */
// @ts-ignore
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import _instance from './axios';
import bath from 'bath-es5';
import find from 'lodash/find';
import pick from 'lodash/pick';
import flatMap from 'lodash/flatMap';
import merge from 'lodash/merge';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isArray from 'lodash/isArray';
import QueryString from 'query-string';
import {
  HttpMethod,
  Operation,
  OperationMethodArguments,
  ParamType,
  RequestConfig,
  UnknownOperationMethod,
  UnknownOperationMethods,
  UnknownPathsDictionary,
} from '~/types/api/client';
import { OperationMethods, PathsDictionary } from '~/types/api/generatedApiClient';
import openapiFromFile from './generated-api.json';
import { API_URL } from '~/env';

/**
 * OpenAPIClient is an AxiosInstance extended with operation methods
 */
export type OpenAPIClient<
  OperationMethods = UnknownOperationMethods,
  PathsDictionary = UnknownPathsDictionary
> = AxiosInstance &
  OperationMethods & {
    paths: PathsDictionary;
  };

let _openapi: any = openapiFromFile;
let _apiClient: OpenAPIClient<OperationMethods, PathsDictionary>;

const getOpenapiClientInstance = () => {
  if (!_apiClient) {
    _apiClient = createAxiosInstance<OpenAPIClient<OperationMethods, PathsDictionary>>();
  }
  return _apiClient;
};

export const apiClient = getOpenapiClientInstance();

/**
 * Creates a new axios instance, extends it and returns it
 *
 * @memberof OpenAPIClientAxios
 */
export function createAxiosInstance<Client = OpenAPIClient>(): Client {
  // create axios instance
  const instance = _instance as OpenAPIClient;

  // create methods for operationIds
  const operations = getOperations();
  for (const operation of operations) {
    const { operationId } = operation;
    if (operationId) {
      instance[operationId] = createOperationMethod(operation);
    }
  }

  // create paths dictionary
  // Example: api.paths['/pets/{id}'].get({ id: 1 });
  instance.paths = {};
  for (const path in _openapi.paths) {
    if (_openapi.paths[path]) {
      if (!instance.paths[path]) {
        instance.paths[path] = {};
      }
      const methods = _openapi.paths[path];
      for (const m in methods) {
        if (methods[m as HttpMethod] && Object.values(HttpMethod).includes(m as HttpMethod)) {
          const method = m as HttpMethod;
          const operation = find(getOperations(), { path, method });
          // @ts-ignore
          instance.paths[path][method] = createOperationMethod(operation);
        }
      }
    }
  }

  return (instance as any) as Client;
}

/**
 * Flattens operations into a simple array of Operation objects easy to work with
 *
 * @returns {Operation[]}
 * @memberof OpenAPIBackend
 */
function getOperations(): Operation[] {
  const paths = _openapi.paths || {};
  return flatMap(Object.entries(paths), ([path, pathObject]) => {
    const methods = pick(pathObject, Object.values(HttpMethod));
    return Object.entries(methods).map(([method, operation]) => {
      const op: Operation = {
        // @ts-ignore
        ...operation,
        path,
        method: method as HttpMethod,
      };
      // @ts-ignore
      if (pathObject.parameters) {
        // @ts-ignore
        op.parameters = [...(op.parameters || []), ...pathObject.parameters];
      }
      // @ts-ignore
      if (pathObject.servers) {
        // @ts-ignore
        op.servers = [...(op.servers || []), ...pathObject.servers];
      }
      return op;
    });
  });
}

/**
 * Creates an axios method for an operation
 * (...pathParams, data?, config?) => Promise<AxiosResponse>
 *
 * @param {Operation} operation
 * @memberof OpenAPIClientAxios
 */
function createOperationMethod(operation: Operation): UnknownOperationMethod {
  return async (...args: OperationMethodArguments) => {
    const axiosConfig = getAxiosConfigForOperation(operation, args);
    // do the axios request
    return _apiClient.request(axiosConfig);
  };
}

/**
 * Creates an axios config object for operation + arguments
 * @memberof OpenAPIClientAxios
 */
function getAxiosConfigForOperation(operation: Operation | string, args: OperationMethodArguments) {
  if (typeof operation === 'string') {
    // @ts-ignore
    operation = getOperation(operation);
  }
  const request = getRequestConfigForOperation(operation, args);

  // construct axios request config
  const axiosConfig: AxiosRequestConfig = {
    // @ts-ignore
    method: request.method,
    url: request.path,
    data: request.payload,
    params: request.query,
    headers: request.headers,
  };

  // allow overriding baseURL with operation / path specific servers
  // @ts-ignore
  const { servers } = operation;
  if (servers && servers[0]) {
    axiosConfig.baseURL = servers[0].url;
  }

  // allow overriding any parameters in AxiosRequestConfig
  const [, , config] = args;
  return config ? merge(axiosConfig, config) : axiosConfig;
}

/**
 * Gets a single operation based on operationId
 *
 * @param {string} operationId
 * @returns {Operation}
 * @memberof OpenAPIBackend
 */
function getOperation(operationId: string): Operation | undefined {
  return find(getOperations(), { operationId });
}

/**
 * Creates a generic request config object for operation + arguments.
 *
 * This function contains the logic that handles operation method parameters.
 *
 * @memberof OpenAPIClientAxios
 */
function getRequestConfigForOperation(
  operation: Operation | string,
  args: OperationMethodArguments
) {
  if (typeof operation === 'string') {
    // @ts-ignore
    operation = getOperation(operation);
  }

  const pathParams = {} as RequestConfig['pathParams'];
  const query = {} as RequestConfig['query'];
  const headers = {} as RequestConfig['headers'];
  const cookies = {} as RequestConfig['cookies'];

  const setRequestParam = (name: string, value: any, type: ParamType | string) => {
    switch (type) {
      case ParamType.Path:
        pathParams[name] = value;
        break;
      case ParamType.Query:
        query[name] = value;
        break;
      case ParamType.Header:
        headers[name] = value;
        break;
      case ParamType.Cookie:
        cookies[name] = value;
        break;
    }
  };

  const getParamType = (paramName: string): ParamType => {
    const param = find((operation as Operation).parameters, {
      name: paramName,
    });
    if (param) {
      // @ts-ignore
      return param.in as ParamType;
    }
    // default all params to query if operation doesn't specify param
    return ParamType.Query;
  };

  const getFirstOperationParam = () => {
    const firstRequiredParam = find((operation as Operation).parameters, {
      required: true,
    });
    if (firstRequiredParam) {
      return firstRequiredParam;
    }
    const firstParam = get((operation as Operation).parameters, '0');
    if (firstParam) {
      return firstParam;
    }
  };

  const [paramsArg, payload] = args;
  if (isArray(paramsArg)) {
    // ParamsArray
    for (const param of paramsArg) {
      setRequestParam(param.name, param.value, param.in || getParamType(param.name));
    }
  } else if (typeof paramsArg === 'object') {
    // ParamsObject
    for (const name in paramsArg) {
      if (paramsArg[name]) {
        setRequestParam(name, paramsArg[name], getParamType(name));
      }
    }
  } else if (!isNil(paramsArg)) {
    const firstParam = getFirstOperationParam();
    if (!firstParam) {
      // @ts-ignore
      throw new Error(`No parameters found for operation ${operation.operationId}`);
    }
    setRequestParam(firstParam.name, paramsArg, firstParam.in as ParamType);
  }

  // path parameters
  // @ts-ignore
  const pathBuilder = bath(operation.path);
  // make sure all path parameters are set
  for (const name of pathBuilder.names) {
    const value = pathParams[name];
    pathParams[name] = `${value}`;
  }
  const path = pathBuilder.path(pathParams);

  // query parameters
  const queryString = QueryString.stringify(query, { arrayFormat: 'none' });

  // full url with query string
  const url = `${API_URL}${path}${queryString ? `?${queryString}` : ''}`;

  // construct request config
  const config: RequestConfig = {
    // @ts-ignore
    method: operation.method,
    url,
    // @ts-ignore
    path,
    pathParams,
    query,
    queryString,
    headers,
    cookies,
    payload,
  };
  return config;
}
